/*
 * Copyright (c) 2024. Tooling Team
 */

import PropTypes from "prop-types";
import { Header } from "@uhg-abyss/web/ui/Header";
import { DropdownMenu } from "@uhg-abyss/web/ui/DropdownMenu";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { NavMenu } from "@uhg-abyss/web/ui/NavMenu";
import { Divider } from "@uhg-abyss/web/ui/Divider";
import { AuthConsumer } from "../../providers/authProvider";
import { useLocation } from "react-router-dom";
import { PATHS } from "../../utils/constants";
import { useGetUserProfileQuery } from "../../app/services/userApi";
import { Fragment, useEffect, useState } from "react";

const menuItems = [
  {
    title: "Log Out",
    onClick: () => {
      window.location.href = "/logout";
    },
  },
];

function UserDropdownMenu({ auth }) {
  const [username, setUsername] = useState("Account");
  useEffect(() => {
    let mounted = true;
    async function fetchUser() {
      try {
        const user = await auth.getUser();
        if (mounted) {
          setUsername(
            user?.profile?.preferred_username ||
              user?.profile?.name ||
              "Account"
          );
        }
      } catch (error) {
        // Optionally log the error and fall back to a safe default username
        // eslint-disable-next-line no-console
        console.error("Failed to fetch authenticated user", error);
        if (mounted) {
          setUsername("Account");
        }
      }
    }
    fetchUser();
    return () => {
      mounted = false;
    };
  }, [auth]);
  return (
    <DropdownMenu
      label={username}
      menuItems={menuItems}
      outline={false}
      before={<IconSymbol icon="account_circle" />}
    />
  );
}

UserDropdownMenu.propTypes = {
  auth: PropTypes.shape({
    getUser: PropTypes.func.isRequired,
  }).isRequired,
};

const NAV_SECTIONS = [
  {
    label: "Maps",
    matchUrls: [PATHS.mapManagement.url],
    items: [
      { label: PATHS.createMap.displayName, url: PATHS.createMap.url },
      {
        label: PATHS.editMap.displayName,
        url: PATHS.editMapMapValueDefinition.url,
      },
    ],
  },
  {
    label: "Builds",
    matchUrls: [PATHS.medDomain.url, PATHS.medDomainEIS.url],
    items: [
      { label: PATHS.medDomain.displayName, url: PATHS.medDomain.url },
      { label: PATHS.medDomainEIS.displayName, url: PATHS.medDomainEIS.url },
    ],
  },
];

function HeaderNavMenu({ auth }) {
  const { pathname } = useLocation();
  const { data: userProfile } = useGetUserProfileQuery();
  const isAdmin = userProfile?.isAdmin === true;

  // Home only matches exactly; the rest stay active on their nested routes.
  const isUrlActive = (url) =>
    url === "/" ? pathname === url : pathname.startsWith(url);

  const navLinkProps = (url) => {
    const isActive = isUrlActive(url);
    return {
      href: url,
      selected: isActive,
      className: isActive ? "mint-header-nav-link-active" : undefined,
    };
  };

  return (
    <NavMenu.Root
      className="mint-header-nav"
      maxNavWidth="none"
      mobileDrawer={false}
      position="end"
      positionViewportToTrigger={{ minWidth: 240 }}
    >
      <NavMenu.List>
        <NavMenu.Link {...navLinkProps(PATHS.home.url)}>Home</NavMenu.Link>
        {NAV_SECTIONS.map((section) => {
          const isActive = section.matchUrls.some(isUrlActive);
          return (
            <NavMenu.Item key={section.label}>
              <NavMenu.Trigger
                className={isActive ? "mint-header-nav-link-active" : undefined}
                selected={isActive}
              >
                {section.label}
              </NavMenu.Trigger>
              <NavMenu.Content>
                <NavMenu.Columns>
                  <NavMenu.Column>
                    {section.items.map((item, index) => (
                      <Fragment key={item.url}>
                        {index > 0 && <NavMenu.Divider />}
                        <NavMenu.MenuItem href={item.url} title={item.label} />
                      </Fragment>
                    ))}
                  </NavMenu.Column>
                </NavMenu.Columns>
              </NavMenu.Content>
            </NavMenu.Item>
          );
        })}
        {isAdmin && (
          <NavMenu.Link {...navLinkProps(PATHS.admin.url)}>Admin</NavMenu.Link>
        )}
        <NavMenu.Item>
          <Divider orientation="vertical" height="24px" />
        </NavMenu.Item>
        <NavMenu.Item className="userMenu">
          <UserDropdownMenu auth={auth} />
        </NavMenu.Item>
      </NavMenu.List>
    </NavMenu.Root>
  );
}

HeaderNavMenu.propTypes = {
  auth: PropTypes.shape({
    getUser: PropTypes.func.isRequired,
  }).isRequired,
};

export function MintHeader({ globalNav = true }) {
  if (globalNav) {
    return (
      <AuthConsumer>
        {(auth) => (
          <Header.Container>
            <div className="d-flex align-items-center gap-2">
              <Header.Brandmark />
              <div className="product-name">
                Mapping Intelligence and Normalization Toolkit
              </div>
            </div>
            <Header.Actions>
              <HeaderNavMenu auth={auth} />
            </Header.Actions>
          </Header.Container>
        )}
      </AuthConsumer>
    );
  }

  return (
    <Header.Container>
      <Header.Brandmark />
    </Header.Container>
  );
}

MintHeader.propTypes = {
  globalNav: PropTypes.bool,
};
